import { isRunningWorkout } from '../../utils/runningWorkoutMetrics';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analyze } from '../../services/api';
import type { WorkoutDetail } from '../../services/api';
import { formatApiDateCompact } from '../../utils/calendarPickerLocale';
import {
  normalizePeakCurve,
  type PeakType,
  type PdcPoint,
} from '../../components/charts/PdcCurveChart';
import {
  compareRangeForPreset,
  type PdcComparePreset,
} from '../../utils/pdcCompareRange';

const DEFAULT_PRESET: PdcComparePreset = 90;

/** Short date label for PDC legend (workout day). */
export function fmtWorkoutDayLabel(iso: string, localeCode = 'en'): string {
  return formatApiDateCompact(iso, localeCode);
}

/** True when workout details include at least one peak curve blob. */
export function workoutHasPeakData(data: WorkoutDetail | null | undefined): boolean {
  const peaks = data?.details?.peaks;
  if (!peaks) return false;
  return (
    normalizePeakCurve(peaks.pw_peak as Record<string, unknown> | undefined).length > 0 ||
    normalizePeakCurve(peaks.hr_peak as Record<string, unknown> | undefined).length > 0 ||
    normalizePeakCurve(peaks.sd_peak as Record<string, unknown> | undefined).length > 0 ||
    normalizePeakCurve(peaks.cd_peak as Record<string, unknown> | undefined).length > 0
  );
}

/** Peak blobs from GET /workout/{id}/details. */
export type WorkoutPeakBlobs = NonNullable<WorkoutDetail['details']>['peaks'];

/** Shared PDC state: current workout curve + historical best comparison. */
export function useWorkoutPdcChart(options: {
  workout: WorkoutDetail;
  userId?: number;
  peaks?: WorkoutPeakBlobs;
}) {
  const { workout, userId, peaks: peaksProp } = options;
  const { i18n } = useTranslation();
  const peaks = peaksProp ?? workout.details?.peaks ?? {};

  const rangeEndDate = useMemo(
    () => (workout.workout_day ?? new Date().toISOString()).slice(0, 10),
    [workout.workout_day],
  );

  const initialRange = useMemo(
    () => compareRangeForPreset(rangeEndDate, DEFAULT_PRESET),
    [rangeEndDate],
  );

  const [pdcPeakType, setPdcPeakType] = useState<PeakType>(isRunningWorkout(workout) ? 'sd_peak' : 'pw_peak');
  const [compareStart, setCompareStart] = useState(initialRange.start);
  const [compareEnd, setCompareEnd] = useState(initialRange.end);
  const [compareCurve, setCompareCurve] = useState<PdcPoint[]>([]);
  const [pdcLoading, setPdcLoading] = useState(false);

  const applyComparePreset = useCallback(
    (preset: PdcComparePreset, custom?: { start: string; end: string }) => {
      const range = compareRangeForPreset(
        rangeEndDate,
        preset,
        custom?.start,
        custom?.end,
      );
      setCompareStart(range.start);
      setCompareEnd(range.end);
    },
    [rangeEndDate],
  );

  useEffect(() => {
    applyComparePreset(DEFAULT_PRESET);
  }, [rangeEndDate, workout.id, applyComparePreset]);

  const currentPdcCurve = useMemo(
    () => normalizePeakCurve(peaks[pdcPeakType] as Record<string, unknown> | undefined, workout.id),
    [peaks, pdcPeakType, workout.id],
  );

  const currentPdcLabel = useMemo(
    () => fmtWorkoutDayLabel(workout.workout_day ?? '', i18n.language),
    [workout.workout_day, i18n.language],
  );

  const showPdc = currentPdcCurve.length > 0 || compareCurve.length > 0 || pdcLoading;

  useEffect(() => {
    if (!userId || !compareStart || !compareEnd) {
      setCompareCurve([]);
      setPdcLoading(false);
      return;
    }

    let cancelled = false;
    setCompareCurve([]);
    setPdcLoading(true);

    analyze
      .getPowerDurationCurve(userId, {
        start_date: compareStart,
        end_date: compareEnd,
        peak_type: pdcPeakType,
        exclude_workout_id: workout.id,
        workout_type_id: workout.workout_type?.id,
      })
      .then((res) => {
        if (cancelled) return;
        const curve = res.data?.data?.curve ?? [];
        setCompareCurve(
          curve.map((c) => ({
            key: c.key,
            interval: c.interval,
            label: c.label,
            value: c.value,
            workoutId: c.workout_id,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setCompareCurve([]);
      })
      .finally(() => {
        if (!cancelled) setPdcLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, compareStart, compareEnd, pdcPeakType, workout.id]);

  return {
    pdcPeakType,
    setPdcPeakType,
    compareStart,
    compareEnd,
    rangeEndDate,
    applyComparePreset,
    compareCurve: isRunningWorkout(workout) && pdcPeakType === 'cd_peak' ? compareCurve.map(p => ({ ...p, value: p.value * 2 })) : compareCurve,
    currentPdcCurve: isRunningWorkout(workout) && pdcPeakType === 'cd_peak' ? currentPdcCurve.map(p => ({ ...p, value: p.value * 2 })) : currentPdcCurve,
    currentPdcLabel,
    pdcLoading,
    showPdc,
  };
}
